import { Component, EventEmitter, Output, Input, forwardRef, ViewChild, NgZone, ChangeDetectorRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CrudFormConfig, CrudFormItem } from '../../utils/edit-form';
import { cloneDeep } from 'lodash';
import { ColumnNumber } from '../crud-view/model/enums';
import { DataDetailViewBuilderFieldsComponent } from './data-detail-view-builder-fields/data-detail-view-builder-fields.component';
import { DataDetailViewBuilderSettingsComponent } from './data-detail-view-builder-settings/data-detail-view-builder-settings.component';
import { NotificationService } from '../../services/notification/notification.service';
import { AlertTypes } from '../../utils/alert/alert-types';
import { TextComponentConfig } from '../../utils/text';
import { HeadingType } from '../../utils/heading';

/**
 * Data Detail View Builder Component
 * Similar to Grid Builder but for configuring detail view
 * Left: Field list with visibility toggle
 * Center: Preview of detail view
 * Right: Settings panel
 */
@Component({
  selector: 'rappider-data-detail-view-builder',
  templateUrl: './data-detail-view-builder.component.html',
  styleUrls: ['./data-detail-view-builder.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DataDetailViewBuilderComponent),
      multi: true
    }
  ]
})
export class DataDetailViewBuilderComponent implements ControlValueAccessor {
  @Output() configChange = new EventEmitter<CrudFormConfig>();
  @Input() previewData: Record<string, unknown> | undefined;

  @ViewChild(DataDetailViewBuilderFieldsComponent) fieldsComponent: DataDetailViewBuilderFieldsComponent;
  @ViewChild(DataDetailViewBuilderSettingsComponent) settingsComponent: DataDetailViewBuilderSettingsComponent;

  // Data model
  _value: CrudFormConfig;
  tempValue: CrudFormConfig;
  tempValueWithoutChanges: CrudFormConfig;
  jsonModalBackupData: CrudFormConfig;
  jsonEditorSaved = false;

  // Initial config - matches detailViewConfig structure
  initialConfig: CrudFormConfig = {
    items: [],
    sections: []
  };

  // Modal state
  isEditModalVisible = false;
  isJsonEditorModalVisible = false;
  modalAutoClose = true;
  modalInitialized = false;
  populatedData: Record<string, unknown>[] = [];

  // Preview column number
  previewColumnNumber = ColumnNumber.TwoColumn;

  // Preview config with only visible fields
  previewConfig: CrudFormConfig = { items: [] };

  // Validation
  validationErrors: { id: string; message: string }[] = [];
  validationAlertVisible = false;
  validationAlertConfig = {
    type: AlertTypes.Error,
    title: {
      type: HeadingType.H5,
      content: 'Required Fields Missing'
    },
    description: <TextComponentConfig>{ text: '' },
    closeable: false,
    showIcon: true
  };

  ColumnNumber = ColumnNumber;

  get value(): CrudFormConfig {
    return this._value;
  }

  set value(value: CrudFormConfig) {
    this._value = value;
    this.onChange(value);
    this.onTouched();
    this.configChange.emit(this.value);
  }

  onChange: (value: CrudFormConfig) => void = () => { };
  onTouched: () => void = () => { };

  constructor(
    private ngZone: NgZone,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef
  ) { }

  writeValue(value: CrudFormConfig): void {
    if (!value) {
      if (!this.tempValue) {
        this.tempValue = cloneDeep(this.initialConfig);
      }
      this._value = cloneDeep(this.initialConfig);
    } else {
      // Ensure all items have visible property set (default to true if undefined)
      const configWithVisible = {
        ...value,
        items: (value.items || []).map(item => ({
          ...item,
          visible: item.visible !== undefined ? item.visible : true
        })),
        // Ensure sections array exists
        sections: value.sections || []
      };
      this._value = cloneDeep(configWithVisible);
      this.tempValue = cloneDeep(configWithVisible);

      // Initialize previewColumnNumber from sections columns if available
      if (configWithVisible.sections && configWithVisible.sections.length > 0) {
        const firstSection = configWithVisible.sections[0];
        if (firstSection.columns) {
          if (firstSection.columns === 1) {
            this.previewColumnNumber = ColumnNumber.OneColumn;
          } else if (firstSection.columns === 2) {
            this.previewColumnNumber = ColumnNumber.TwoColumn;
          } else if (firstSection.columns === 3) {
            this.previewColumnNumber = ColumnNumber.ThreeColumn;
          }
        }
      }
    }
    this.tempValueWithoutChanges = cloneDeep(this.tempValue);
    // Initialize preview config
    this.updatePreviewConfig();
  }

  registerOnChange(fn: (value: CrudFormConfig) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * Handle settings change from settings component
   */
  onSettingsChange(newConfig: CrudFormConfig): void {
    if (!newConfig) {
      return;
    }
    this.tempValue = cloneDeep(newConfig);
    if (!this.tempValue.items) {
      this.tempValue.items = [];
    }
    if (!this.tempValue.sections) {
      this.tempValue.sections = [];
    }
    // Update preview config with new settings
    this.updatePreviewConfig();
    if (this.validationAlertVisible) {
      this.updateValidationMessages();
    }
    this.cdr.detectChanges();
  }

  /**
   * Handle items change (kept for backward compatibility)
   */
  onItemsChange(): void {
    this.tempValue = { ...this.tempValue };
    this.updatePreviewConfig();
    if (this.validationAlertVisible) {
      this.updateValidationMessages();
    }
  }

  onFieldsChange(newItems?: CrudFormItem[]): void {
    if (!this.tempValue) {
      return;
    }
    // Update tempValue with new items array (use newItems if provided, otherwise use current)
    const itemsToUse = newItems || this.tempValue.items || [];
    this.tempValue = {
      ...this.tempValue,
      items: [...itemsToUse] // Create new array reference for change detection
    };
    this.updatePreviewConfig();
    if (this.validationAlertVisible) {
      this.updateValidationMessages();
    }
    this.cdr.detectChanges();
  }

  /**
   * Handle populated data changes from fields component
   */
  onPopulatedDataChange(populatedData: Record<string, unknown>[]): void {
    this.populatedData = populatedData;
    // Update preview config to only show visible fields
    this.updatePreviewConfig();
  }

  /**
   * Get preview data - use previewData if available, otherwise use populatedData
   */
  getPreviewData(): Record<string, unknown> | undefined {
    if (this.previewData) {
      return this.previewData;
    }
    return this.populatedData && this.populatedData.length > 0 ? this.populatedData[0] : undefined;
  }

  /**
   * Update preview config to only include visible fields
   * Maintains the order from tempValue.items and updates index property
   * Also preserves all settings like showInCollapsedSections and defaultSectionTitle
   */
  private updatePreviewConfig(): void {
    if (!this.tempValue?.items) {
      this.previewConfig = {
        items: [],
        sections: this.tempValue?.sections || [],
        showInCollapsedSections: this.tempValue?.showInCollapsedSections,
        defaultSectionTitle: this.tempValue?.defaultSectionTitle
      };
      return;
    }
    // Filter to only visible items for preview (explicitly check for true)
    // Maintain the order from tempValue.items
    const visibleItems = this.tempValue.items
      .filter(item => item.visible === true)
      .map((item, index) => ({
        ...item,
        index: index // Update index to reflect new order
      }));
    // Create a new config object for preview - preserve all settings
    this.previewConfig = {
      ...this.tempValue,
      items: visibleItems,
      sections: this.tempValue.sections || [],
      showInCollapsedSections: this.tempValue.showInCollapsedSections,
      defaultSectionTitle: this.tempValue.defaultSectionTitle
    };
    this.cdr.detectChanges();
  }

  /**
   * Save configuration
   */
  onConfigSave(): void {
    this.validationErrors = [];

    if (!this.validateConfig(true)) {
      this.modalAutoClose = false;
      this.ngZone.run(() => {
        this.modalAutoClose = true;
      });
      return;
    }

    this.validationAlertVisible = false;
    this.validationErrors = [];
    this.modalAutoClose = true;
    this.value = cloneDeep(this.tempValue);
    this.tempValueWithoutChanges = cloneDeep(this.tempValue);
    this.closeEditModal();
  }

  onCancel(): void {
    this.closeEditModal();
  }

  /**
   * Open modal to edit configuration
   */
  openEditModal(): void {
    this.validationErrors = [];
    this.validationAlertVisible = false;
    this.modalInitialized = false;

    if (this._value) {
      this.tempValue = cloneDeep(this._value);
    } else {
      this.tempValue = cloneDeep(this.initialConfig);
    }

    if (!this.tempValue.items) {
      this.tempValue.items = [];
    }

    if (!this.tempValue.sections) {
      this.tempValue.sections = [];
    }

    // Ensure all items have visible property set (default to true if undefined)
    this.tempValue.items = this.tempValue.items.map(item => ({
      ...item,
      visible: item.visible !== undefined ? item.visible : true
    }));

    this.tempValueWithoutChanges = cloneDeep(this.tempValue);
    this.updatePreviewConfig();
    this.isEditModalVisible = true;
    this.cdr.detectChanges();
  }

  /**
   * Handler for modal after open event
   */
  onModalAfterOpen(): void {
    if (this.modalInitialized) {
      return;
    }
    this.modalInitialized = true;
    this.cdr.detectChanges();
  }

  /**
   * Close edit modal and reset states
   */
  closeEditModal(): void {
    this.isEditModalVisible = false;
    this.validationErrors = [];
    this.validationAlertVisible = false;
    this.modalInitialized = false;
    // Restore original value if cancelled
    if (this.tempValueWithoutChanges) {
      this.tempValue = cloneDeep(this.tempValueWithoutChanges);
    }
  }

  /**
   * Handle data change from JSON editor
   */
  onDataChange(event: CrudFormConfig): void {
    if (event) {
      this.tempValue = cloneDeep(event);
      this.jsonEditorSaved = true;
      this.updatePreviewConfig();
      this.checkIfErrorsResolved();
    }
  }

  /**
   * Open JSON editor modal
   */
  openJsonEditorModal(): void {
    this.jsonModalBackupData = cloneDeep(this.tempValue);
    this.jsonEditorSaved = false;
    this.isJsonEditorModalVisible = true;
  }

  /**
   * Close JSON editor modal
   */
  closeJsonEditorModal(): void {
    this.isJsonEditorModalVisible = false;

    if (!this.tempValue) {
      this.tempValue = this.jsonModalBackupData;
    }

    this.value = this.tempValue;
    this.tempValueWithoutChanges = this.tempValue;
    this.checkIfErrorsResolved();
  }

  /**
   * Handle JSON editor visibility change
   */
  onJsonEditorVisibleChange(visible: boolean): void {
    this.isJsonEditorModalVisible = visible;

    if (!visible) {
      if (!this.jsonEditorSaved && this.jsonModalBackupData) {
        this.tempValue = cloneDeep(this.jsonModalBackupData);
      }
      this.jsonEditorSaved = false;
    }
  }

  /**
   * Update preview column number
   * Also updates tempValue sections to reflect the column change
   */
  onPreviewColumnNumberChange(columnNumber: ColumnNumber): void {
    this.previewColumnNumber = columnNumber;
    const columnCount = columnNumber as number;
    if (this.tempValue) {
      let sections = this.tempValue.sections ? [...this.tempValue.sections] : [];
      if (sections.length > 0) {
        sections = sections.map(section => ({
          ...section,
          columns: columnCount
        }));
      } else {
        if (this.tempValue.items && this.tempValue.items.length > 0) {
          const sectionNames = this.tempValue.items
            .map(item => item.section)
            .filter(section => section && typeof section === 'string')
            .map(section => section.trim())
            .filter(section => section.length > 0);
          const uniqueSectionNames = [...new Set(sectionNames)];

          if (uniqueSectionNames.length > 0) {
            sections = uniqueSectionNames.map(sectionName => ({
              key: sectionName,
              columns: columnCount
            }));
          }
        }
      }
      this.tempValue = {
        ...this.tempValue,
        sections: sections
      };
      this.updatePreviewConfig();
    }
  }

  /**
   * Validate configuration
   */
  private validateConfig(isSaveOperation = false): boolean {
    if (!this.tempValue?.items || this.tempValue.items.length === 0) {
      this.validationErrors = [{
        id: 'no-items',
        message: 'Please add at least one field to the configuration.'
      }];

      if (isSaveOperation) {
        this.updateValidationAlert();
        this.validationAlertVisible = true;
        this.notificationService.createNotification(
          'error',
          'Validation Error',
          'Please add at least one field to proceed.'
        );
      }
      return false;
    }

    // Check for required fields in items
    const missingFields: string[] = [];
    this.tempValue.items.forEach((item, index) => {
      const itemNumber = index + 1;
      if (!item.fieldName) {
        missingFields.push(`Field ${itemNumber}: Field name is required`);
      }
      if (!item.type) {
        missingFields.push(`Field ${itemNumber}: Type is required`);
      }
    });

    if (missingFields.length > 0) {
      this.validationErrors = [];
      this.addValidationError('validation-errors', '');

      this.validationAlertConfig = {
        type: AlertTypes.Error,
        title: {
          type: HeadingType.H5,
          content: 'Required Fields Missing'
        },
        description: <TextComponentConfig>{ text: missingFields.join('\n') },
        showIcon: true,
        closeable: false
      };

      if (isSaveOperation) {
        this.validationAlertVisible = true;
        this.notificationService.createNotification(
          'error',
          'Validation Error',
          'Please complete all required fields to save the configuration.'
        );
      }

      return false;
    }

    return true;
  }

  /**
   * Update validation messages
   */
  private updateValidationMessages(): void {
    const missingFields: string[] = [];

    if (this.tempValue?.items) {
      this.tempValue.items.forEach((item, index) => {
        const itemNumber = index + 1;
        if (!item.fieldName) {
          missingFields.push(`Field ${itemNumber}: Field name is required`);
        }
        if (!item.type) {
          missingFields.push(`Field ${itemNumber}: Type is required`);
        }
      });
    }

    if (missingFields.length > 0) {
      this.validationAlertConfig = {
        type: AlertTypes.Error,
        title: {
          type: HeadingType.H5,
          content: 'Required Fields Missing'
        },
        description: <TextComponentConfig>{ text: missingFields.join('\n') },
        showIcon: true,
        closeable: false
      };
    } else {
      this.validationAlertVisible = false;
      this.validationErrors = [];
    }
  }

  /**
   * Add validation error
   */
  private addValidationError(id: string, message: string): void {
    if (!this.validationErrors.some(error => error.id === id)) {
      this.validationErrors.push({ id, message });
    }
  }

  /**
   * Check if errors are resolved
   */
  private checkIfErrorsResolved(): void {
    if (!this.validationAlertVisible) {
      return;
    }

    const isValid = this.validateConfig(false);

    if (isValid) {
      this.validationAlertVisible = false;
      this.validationErrors = [];
    }
  }

  /**
   * Update validation alert
   */
  private updateValidationAlert(): void {
    if (this.validationErrors.length > 0) {
      const errorMessages = this.validationErrors
        .filter(error => error.message)
        .map(error => error.message);

      this.validationAlertConfig = {
        type: AlertTypes.Error,
        title: {
          type: HeadingType.H5,
          content: 'Required Fields Missing'
        },
        description: <TextComponentConfig>{ text: errorMessages.join('\n') },
        showIcon: true,
        closeable: false
      };
      this.validationAlertVisible = true;
    } else {
      this.validationAlertVisible = false;
    }
  }
}

